import React, { useEffect, useState } from "react";
import { Modal } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonButton from "../button";
import CommonDatePicker from "../datepicker";
import CommonDropDown from "../dropdown";
import {
  getPaymentDownloadAction,
  resetPaymentDownloadAction,
} from "../../containers/dashboard/action";

// Optional month day-range cycle. The API key stays `month_day_range_type`; the
// first entry is a "no preference" default that sends nothing.
const MONTH_RANGE_DEFAULT = { value: "", label: "Default" };
const MONTH_RANGE_OPTIONS = [
  MONTH_RANGE_DEFAULT,
  { value: "MONTH_TO_MONTH_DAY_RANGE_TYPE", label: "Month to Month Day Range" },
  { value: "MONTH_DAY_RANGE_TYPE", label: "Month Day Range" },
];

function getModalStyle() {
  const top = 50;
  const left = 50;
  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles(() => ({
  modalPaper: { position: "absolute" },
  container: {
    width: "420px",
    backgroundColor: theme.colors.White,
    boxShadow: "0px 0px 10px #CFD9EA80",
    borderRadius: "12px",
    padding: "40px 45px",
    boxSizing: "border-box",
  },
  title: {
    color: theme.colors.DodgerBlue,
    fontSize: "22px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "6px",
  },
  subtitle: {
    color: theme.colors.Violet,
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    marginBottom: "24px",
  },
  fieldRow: {
    display: "flex",
    gap: "18px",
    flexWrap: "wrap",
    marginBottom: "10px",
  },
  field: { display: "flex", flexDirection: "column" },
  label: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    marginBottom: "6px",
  },
  clearLink: {
    display: "inline-block",
    marginTop: "4px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.DodgerBlue,
    cursor: "pointer",
  },
  errorBox: {
    backgroundColor: "#FDECEC",
    border: `1px solid ${theme.colors.Pomegranate}`,
    borderRadius: "8px",
    padding: "10px 12px",
    margin: "6px 0 18px",
  },
  errorLine: {
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Pomegranate,
  },
  rangeTypeField: {
    display: "flex",
    flexDirection: "column",
    marginTop: "18px",
  },
  rangeTypeHint: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: "#8892a4",
    marginTop: "6px",
  },
  hint: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: "#8892a4",
    margin: "8px 0 20px",
  },
  actions: { display: "flex", gap: "12px", marginTop: "10px" },
}));

// Modal for the "Download all report" action. Collects an optional date range
// (start_date / end_date -> start_time / end_time query params). Both are
// optional, but if either is entered BOTH are required and start must not be
// after end. Leaving both blank downloads the full history.
function DownloadAllReportModal({ open, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [modalStyle] = useState(getModalStyle);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthRange, setMonthRange] = useState(MONTH_RANGE_DEFAULT);
  const [validationError, setValidationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const csv = useSelector((state) => state.dashboardReducer?.csv);
  const error = useSelector((state) => state.dashboardReducer?.error);

  const resetLocal = () => {
    setStartDate("");
    setEndDate("");
    setMonthRange(MONTH_RANGE_DEFAULT);
    setValidationError(null);
    setSubmitting(false);
  };

  // Clear stale state whenever the modal opens.
  useEffect(() => {
    if (open) {
      setValidationError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Download finished successfully (the saga already triggered the file save) —
  // reset the download state and close.
  useEffect(() => {
    if (submitting && csv) {
      resetLocal();
      dispatch(resetPaymentDownloadAction());
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csv, submitting]);

  // Download failed — surface it and let the user retry.
  useEffect(() => {
    if (submitting && error) {
      setSubmitting(false);
      const messages = Array.isArray(error) ? error : [error];
      setValidationError(
        messages.filter(Boolean).join(" ") || "Unable to download the report."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, submitting]);

  const handleClose = () => {
    if (submitting) return; // don't close mid-download
    resetLocal();
    onClose();
  };

  const handleDownload = () => {
    setValidationError(null);

    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    // Both optional — but if one is given, the other is required.
    if (hasStart !== hasEnd) {
      setValidationError("Please enter both a start and end date, or leave both blank.");
      return;
    }
    // Ordering guard (only relevant when both are provided).
    if (hasStart && hasEnd && startDate > endDate) {
      setValidationError("Start date can't be after the end date.");
      return;
    }

    dispatch(
      getPaymentDownloadAction(undefined, {
        start_time: startDate || undefined,
        end_time: endDate || undefined,
        month_day_range_type: monthRange?.value || undefined,
      })
    );
    setSubmitting(true);
  };

  return (
    <Modal open={!!open} onClose={handleClose}>
      <div style={modalStyle} className={classes.modalPaper}>
        <div className={classes.container}>
          <div className={classes.title}>Download all report</div>
          <div className={classes.subtitle}>
            Optionally pick a date range, or leave both blank to download the full
            history.
          </div>

          {validationError ? (
            <div className={classes.errorBox}>
              <div className={classes.errorLine}>{validationError}</div>
            </div>
          ) : null}

          <div className={classes.fieldRow}>
            <div className={classes.field}>
              <span className={classes.label}>Start date</span>
              <CommonDatePicker
                startDate={startDate}
                setStartDate={setStartDate}
                maxDate={endDate || undefined}
              />
            </div>
            <div className={classes.field}>
              <span className={classes.label}>End date</span>
              <CommonDatePicker
                startDate={endDate}
                setStartDate={setEndDate}
                minDate={startDate || undefined}
              />
            </div>
          </div>

          {startDate || endDate ? (
            <span
              className={classes.clearLink}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setValidationError(null);
              }}
            >
              Clear dates
            </span>
          ) : null}
          <div className={classes.hint}>
            Leaving both blank downloads every record.
          </div>

          <div className={classes.rangeTypeField}>
            <span className={classes.label}>Month range type</span>
            <CommonDropDown
              placeholder="Default"
              options={MONTH_RANGE_OPTIONS}
              value={monthRange}
              onChange={(option) => setMonthRange(option || MONTH_RANGE_DEFAULT)}
            />
            <div className={classes.rangeTypeHint}>
              Use “Month to Month Day Range” for reports whose cycle overlaps into
              the next month; “Month Day Range” for cycles kept within a month.
            </div>
          </div>

          

          <div className={classes.actions}>
            <CommonButton
              type="button"
              text="Download"
              stylevariant="primary"
              loading={submitting}
              disabled={submitting}
              onClick={handleDownload}
            />
            <CommonButton
              type="button"
              text="Cancel"
              stylevariant="secondary2"
              disabled={submitting}
              onClick={handleClose}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default DownloadAllReportModal;
